import React from 'react';
import SharedPageLayout from './layout/SharedPageLayout';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Wand2,
  TestTube2,
  Workflow,
  BarChart3,
  Shield,
  Zap,
  Users,
  Clock,
  Globe,
  Code2,
  GitBranch,
  Play,
  CheckCircle2
} from 'lucide-react';

const Features = ({ onBack, onLogin, onRegister, navigateTo }) => {
  const features = [
    {
      icon: Wand2,
      title: 'AI-Powered Test Generation',
      description: 'Write tests in plain English. Our AI converts your descriptions into production-ready Playwright tests automatically.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Workflow,
      title: 'Visual Flow Builder',
      description: 'Build complex test flows with our intuitive drag-and-drop interface. No coding required.',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Play,
      title: 'Live Test Execution',
      description: 'Watch your tests run in real-time with our integrated VNC viewer. Debug issues instantly as they happen.',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: TestTube2,
      title: 'API Testing',
      description: 'Create and execute API tests with AI assistance. Support for REST, GraphQL, and SOAP with data chaining.',
      gradient: 'from-orange-500 to-amber-500'
    },
    {
      icon: GitBranch,
      title: 'CI/CD Integration',
      description: 'Seamlessly integrate with GitHub Actions, Jenkins, GitLab CI, and more. Run tests on every push.',
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      icon: Clock,
      title: 'Test Scheduling',
      description: 'Schedule tests to run automatically. Daily, weekly, or on every deployment with detailed reports.',
      gradient: 'from-rose-500 to-pink-500'
    },
    {
      icon: BarChart3,
      title: 'Sprint QA Dashboard',
      description: 'Track your entire QA lifecycle. Manage requirements, test cases, and coverage in one place.',
      gradient: 'from-violet-500 to-purple-500'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Invite team members, assign roles, and collaborate on test suites with real-time updates.',
      gradient: 'from-sky-500 to-blue-500'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Manage workspace roles, project access, and security logs.',
      gradient: 'from-slate-500 to-gray-600'
    },
    {
      icon: Globe,
      title: 'Multi-Tenant Workspaces',
      description: 'Enterprise-grade workspace isolation. Each team gets their own subdomain and data separation.',
      gradient: 'from-teal-500 to-green-500'
    },
    {
      icon: Code2,
      title: 'Code Export',
      description: 'Export your tests as clean Playwright code. Own your test automation without vendor lock-in.',
      gradient: 'from-amber-500 to-orange-500'
    },
    {
      icon: Zap,
      title: 'Parallel Execution',
      description: 'Run tests in parallel across multiple browsers and devices. Get results in minutes, not hours.',
      gradient: 'from-yellow-500 to-amber-500'
    }
  ];

  return (
    <SharedPageLayout
      title="Features - AI-Powered Test Automation | Lexa.run"
      description="Explore Lexa.run's powerful features: AI test generation, visual flow builder, API testing, CI/CD integration, and more."
      onBack={onBack}
      onLogin={onLogin}
      onRegister={onRegister}
      navigateTo={navigateTo}
    >
      {/* Hero Section */}
      <section className="relative pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-30" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-200 rounded-full blur-3xl opacity-30" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="outline" className="mb-4 text-purple-600 border-purple-200">
            Platform Features
          </Badge>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-purple-800 to-gray-900">
              Everything You Need
            </span>
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
              To Test Smarter
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From AI-powered test generation to comprehensive QA lifecycle management. 
            Lexa.run provides all the tools your team needs to ship quality software faster.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="group relative overflow-hidden p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-gray-200/50"
              >
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity bg-gradient-to-br ${feature.gradient}`} />
                
                <div className="relative z-10">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 shadow-lg`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-purple-700 transition-colors">
                    {feature.title}
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to experience these features?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Start with 5 free AI-generated tests. No credit card required.
          </p>
          <button 
            onClick={onRegister}
            className="inline-flex items-center px-8 py-4 rounded-full bg-white text-purple-600 hover:bg-gray-100 font-semibold shadow-xl transition-all hover:-translate-y-1"
          >
            <CheckCircle2 className="w-5 h-5 mr-2" />
            Get Started Free
          </button>
        </div>
      </section>
    </SharedPageLayout>
  );
};

export default Features;
